import {
  Children,
  isValidElement,
  type ReactElement,
  type ReactNode,
  useMemo,
} from 'react';

import { Box, useTheme } from '@mui/material';
import GridLayout, {
  useContainerWidth,
  verticalCompactor,
} from 'react-grid-layout';
import { transformStrategy } from 'react-grid-layout/core';

import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

import { tokens } from '../../../theme';
import { COLS, ROW_HEIGHT } from '../_constants';
import type { BaseDynamicGridProps } from '../types';

const DEFAULT_PADDING: [number, number] = [0, 0];

// React.Children.map prefixes keys (e.g. "clusters" → ".$clusters"), breaking
// react-grid-layout's key→layout matching. Using forEach+push preserves the
// original keys on the wrapper divs.
const wrapChildren = (children: ReactNode): ReactElement[] => {
  const wrapped: ReactElement[] = [];
  Children.forEach(children, (child) => {
    if (isValidElement(child)) {
      wrapped.push(
        <div key={child.key} style={{ width: '100%', height: '100%' }}>
          {child}
        </div>
      );
    }
  });
  return wrapped;
};

export const BaseDynamicGrid = ({
  layout,
  maxRows,
  colGap = 16,
  rowGap = 16,
  containerPadding = DEFAULT_PADDING,
  isCustomizing = false,
  onLayoutChange,
  rowHeight = ROW_HEIGHT,
  children,
}: BaseDynamicGridProps) => {
  const theme = useTheme();

  const { containerRef, width, mounted } = useContainerWidth({
    measureBeforeMount: true,
  });

  const gridConfig = useMemo(
    () => ({
      cols: COLS,
      rowHeight,
      margin: [colGap, rowGap] as [number, number],
      containerPadding,
      ...(maxRows !== undefined && { maxRows }),
    }),
    [colGap, rowGap, containerPadding, maxRows, rowHeight]
  );

  const dragConfig = useMemo(
    () =>
      isCustomizing
        ? { enabled: true, cancel: '.MuiButtonBase-root, a, button' }
        : { enabled: false, cancel: '.MuiButtonBase-root, a, button' },
    [isCustomizing]
  );

  const resizeConfig = useMemo(
    () => ({ enabled: isCustomizing, handles: ['se'] as const }),
    [isCustomizing]
  );

  const wrappedChildren = useMemo(() => wrapChildren(children), [children]);
  const gridZIndex = isCustomizing ? theme.zIndex.sidebar + 1 : 'auto';

  const sx = useMemo(
    () => ({
      position: 'relative',
      mb: isCustomizing ? 88 : 0,
      zIndex: gridZIndex,
      '& .react-grid-item': {
        cursor: isCustomizing ? 'grab' : 'default',
        userSelect: 'none',
      },
      '& .react-grid-item:active': {
        cursor: isCustomizing ? 'grabbing' : 'default',
      },

      '& .react-grid-item.react-draggable-dragging': {
        zIndex: 10,
      },
      '& .react-grid-item.react-grid-placeholder': {
        background: tokens.theme.data.blue300,
        opacity: 0.2,
        borderRadius: tokens.theme.radius.sm,
      },
      '& .react-resizable-handle': {
        opacity: 0,
        transition: 'opacity 0.15s',
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16'%3E%3Cpath d='M3 13L13 3M7 13L13 7M11 13L13 11' stroke='rgba(0%2C0%2C0%2C0.3)' stroke-width='1.5' stroke-linecap='round'/%3E%3C%2Fsvg%3E")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'bottom right',
        backgroundSize: '16px 16px',
        width: '20px',
        height: '20px',
        bottom: '4px',
        right: '4px',
      },
      '& .react-resizable-handle::after': {
        display: 'none',
      },
      '& .react-grid-item:hover .react-resizable-handle': {
        opacity: isCustomizing ? 1 : 0,
      },
    }),
    [gridZIndex, isCustomizing]
  );

  return (
    <Box ref={containerRef} sx={sx}>
      {mounted && width > 0 && (
        <GridLayout
          layout={layout}
          width={width}
          gridConfig={gridConfig}
          dragConfig={dragConfig}
          resizeConfig={resizeConfig}
          positionStrategy={transformStrategy}
          compactor={verticalCompactor}
          onLayoutChange={onLayoutChange}
        >
          {wrappedChildren}
        </GridLayout>
      )}
    </Box>
  );
};
